import { Localized, useLocalization } from "@fluent/react";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { ReactNode, useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
    Box,
    ButtonProps,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    IconButton,
    Typography,
} from "@mui/material";
import { CancelButton } from "../../app/components/CancelButton";

export type BackgroundProcessingQuestionDialogContent = ReactNode | ((fullScreen: boolean) => ReactNode);

interface Props {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: ReactNode;
    description?: ReactNode;
    children?: BackgroundProcessingQuestionDialogContent;
    loading?: boolean;
    maxWidth?: DialogProps["maxWidth"];
    allowFullScreen?: boolean;
    disableCloseWhileLoading?: boolean;
    confirmLabel?: ReactNode;
    confirmLabelId?: string;
    confirmColor?: ButtonProps["color"];
    confirmVariant?: ButtonProps["variant"];
    confirmStartIcon?: ReactNode;
    confirmDisabled?: boolean;
    cancelLabel?: ReactNode;
    cancelLabelId?: string;
    cancelDisabled?: boolean;
}

export const BackgroundProcessingQuestionDialog = ({
    open,
    onClose,
    onConfirm,
    title,
    description,
    children,
    loading = false,
    maxWidth = "sm",
    allowFullScreen = false,
    disableCloseWhileLoading = true,
    confirmLabel = "Run",
    confirmLabelId,
    confirmColor = "primary",
    confirmVariant = "contained",
    confirmStartIcon,
    confirmDisabled = false,
    cancelDisabled = false,
}: Props) => {
    const { l10n } = useLocalization();
    const [fullScreen, setFullScreen] = useState(false);

    useEffect(() => {
        if (!open) {
            setFullScreen(false);
        }
    }, [open]);

    const handleClose = (_event?: object, reason?: string) => {
        if (reason === "backdropClick") {
            return;
        }

        if (disableCloseWhileLoading && loading) {
            return;
        }

        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth={maxWidth} fullScreen={fullScreen}>
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>{title}</Box>
                    {allowFullScreen && (
                        <IconButton
                            size="small"
                            color="primary"
                            aria-label={l10n.getString(
                                fullScreen ? "toggle-fullscreen-exit" : "toggle-fullscreen-enter",
                                null,
                                fullScreen ? "Exit fullscreen mode" : "Enter fullscreen mode"
                            )}
                            title={l10n.getString(
                                fullScreen ? "toggle-fullscreen-exit" : "toggle-fullscreen-enter",
                                null,
                                fullScreen ? "Exit fullscreen mode" : "Enter fullscreen mode"
                            )}
                            onClick={() => setFullScreen((current) => !current)}
                        >
                            {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                    )}
                </Box>
            </DialogTitle>
            <DialogContent
                sx={fullScreen ? { display: "flex", flexDirection: "column", minHeight: 0 } : undefined}
            >
                {description && (
                    <Box
                        sx={{
                            mt: 0.5,
                            mb: 2,
                            px: 1.5,
                            py: 1.25,
                            bgcolor: "action.hover",
                            border: 1,
                            borderColor: "divider",
                            borderLeft: 4,
                            borderLeftColor: "info.main",
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500, lineHeight: 1.6 }}>
                            {description}
                        </Typography>
                    </Box>
                )}
                {typeof children === "function" ? children(fullScreen) : children}
            </DialogContent>
            <DialogActions>

                <LoadingButton
                    loading={loading}
                    variant={confirmVariant}
                    color={confirmColor}
                    disabled={confirmDisabled}
                    startIcon={confirmStartIcon}
                    size="small"
                    onClick={onConfirm}
                >
                    {confirmLabelId ? (
                        <Localized id={confirmLabelId}>
                            <span>{confirmLabel}</span>
                        </Localized>
                    ) : (
                        "Run"
                    )}
                </LoadingButton>
                <CancelButton
                    onClick={handleClose}
                    loading={false}
                    disabled={loading || cancelDisabled}
                />

            </DialogActions>
        </Dialog>
    );
};